import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "62";
const height = "40";
const viewBox = "0 0 62 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.orca}
            d="M16.653 37.797c0 1.074-.576 1.62-1.48 1.62-.934 0-1.502-.595-1.502-1.655v-2.82h-.554v2.855c0 1.39.828 2.133 2.041 2.133 1.228 0 2.05-.744 2.05-2.175v-2.813h-.555v2.855zm4.065-2.89l-2.238 4.946h.568l.582-1.298h2.674l.575 1.298h.596l-2.238-4.946h-.52zm-.87 3.143l1.123-2.498 1.115 2.498h-2.238zm23.628-3.108h-1.838v4.911h.554V38.12h1.193c1.045 0 1.929-.547 1.929-1.607V36.5c0-.968-.73-1.557-1.838-1.557zm1.277 1.593c0 .645-.533 1.08-1.347 1.08h-1.214v-2.16h1.242c.792 0 1.32.364 1.32 1.065v.015zm-33.533.863v-.015c0-1.353-1.003-2.525-2.498-2.525-1.494 0-2.511 1.185-2.511 2.54v.013c0 1.354 1.003 2.526 2.498 2.526.603 0 1.129-.197 1.543-.52l.652.583.379-.414-.645-.547a2.618 2.618 0 00.582-1.642zm-.99 1.297l-.918-.835-.372.415.919.785a1.88 1.88 0 01-1.137.365c-1.122 0-1.936-.912-1.936-2.027v-.015c0-1.115.8-2.013 1.923-2.013 1.122 0 1.935.912 1.935 2.028v.013c0 .492-.147.933-.413 1.285zm-7.194-1.564c-1.08-.231-1.333-.491-1.333-.954v-.014c0-.443.407-.793 1.045-.793.505 0 .962.162 1.418.533l.322-.428c-.498-.4-1.01-.603-1.725-.603-.934 0-1.614.568-1.614 1.34v.014c0 .807.519 1.172 1.648 1.417 1.032.217 1.277.477 1.277.933v.014c0 .484-.435.835-1.094.835-.68 0-1.172-.231-1.684-.694l-.344.406c.59.526 1.228.786 2.007.786.975 0 1.677-.547 1.677-1.389v-.014c0-.75-.505-1.15-1.6-1.39zm55.625 2.217v-1.72h2.68v-.504h-2.68v-1.677h2.996v-.505h-3.55v4.911h3.585v-.505h-3.031zm-20.52-2.217c-1.08-.231-1.334-.491-1.334-.954v-.014c0-.443.407-.793 1.046-.793.505 0 .96.162 1.417.533l.323-.428c-.498-.4-1.01-.603-1.726-.603-.934 0-1.614.568-1.614 1.34v.014c0 .807.52 1.172 1.649 1.417 1.031.217 1.276.477 1.276.933v.014c0 .484-.434.835-1.094.835-.68 0-1.172-.231-1.683-.694l-.344.406c.589.526 1.227.786 2.006.786.976 0 1.677-.547 1.677-1.389v-.014c0-.75-.505-1.15-1.6-1.39zm-6.676.498h2.68v-.505h-2.68v-1.677h2.996v-.505h-3.55v4.911h3.585v-.505h-3.03v-1.72zm16.673-2.722l-2.237 4.946h.567l.583-1.298h2.673l.575 1.298h.597l-2.238-4.946h-.52zm-.87 3.143l1.123-2.498 1.115 2.498H47.27zm7.082 1.375c-1.08 0-1.9-.89-1.9-2.027v-.015c0-1.13.813-2.013 1.9-2.013.667 0 1.13.28 1.544.673l.38-.406c-.492-.464-1.026-.78-1.916-.78-1.445 0-2.484 1.152-2.484 2.54v.014c0 1.41 1.046 2.526 2.463 2.526.89 0 1.452-.343 1.964-.863l-.365-.358c-.47.45-.912.709-1.586.709zm-25.427-3.01v-.014c0-.386-.14-.73-.38-.968-.308-.309-.792-.491-1.395-.491h-2.112v4.911h.554v-1.909h1.403l1.424 1.909h.68l-1.508-2.007c.772-.14 1.334-.617 1.334-1.43zm-3.333 1.032v-1.993h1.516c.792 0 1.255.365 1.255.968v.014c0 .631-.526 1.01-1.263 1.01H25.59zm-5.635-23.354c.447.447 1.17.447 1.617 0l10.67-10.67a3.892 3.892 0 015.497-.001 1.143 1.143 0 101.617-1.617 6.18 6.18 0 00-8.73 0l-10.67 10.671a1.143 1.143 0 000 1.617zm5.173 3.556l10.67-10.67a1.143 1.143 0 10-1.616-1.617l-10.67 10.67a3.892 3.892 0 01-5.497.001 3.892 3.892 0 010-5.498l8.583-8.583A1.143 1.143 0 1024.982.335L16.398 8.92a6.181 6.181 0 000 8.73 6.18 6.18 0 008.73 0zm7.114 7.114l10.67-10.67a1.142 1.142 0 10-1.617-1.617l-10.67 10.67a3.891 3.891 0 01-5.497 0 1.143 1.143 0 10-1.616 1.618 6.18 6.18 0 008.73-.001zm5.644 1.47l8.583-8.583a6.181 6.181 0 000-8.731 6.18 6.18 0 00-8.73 0l-10.67 10.67a1.142 1.142 0 101.617 1.617l10.67-10.67a3.892 3.892 0 015.496 0 3.892 3.892 0 010 5.497l-8.583 8.584a1.143 1.143 0 101.617 1.616z"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g, &&& circle",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Squarespace"
});
